import { get } from 'lodash';
import React, { forwardRef, useRef, useState } from 'react';
import {
  Controller,
  FieldError,
  FieldValues,
  FieldName,
  UseFormMethods,
} from 'react-hook-form';
import uploadImage from '../../../assets/upload.png';
import reloadImage from '../../../assets/reload.png';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../../../types/utilTypes';
import { getValidationErrorMessage } from '../../../utils/validation';
import { Button } from '../Button/Button';
import styles from './styles.module.scss';

const IMAGE_VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

interface Props {
  label: string | React.ReactNode;
  value?: string;
  helperText?: string;
  errorText?: string;
  fileSizeLimit: number;
  imageWidthLimit: number;
  imageHeightLimit: number;
  onChange?: (base64Image: string | undefined) => void;
}
const ImageUpload = forwardRef<HTMLDivElement, Props>(
  (
    {
      label,
      value,
      helperText,
      errorText,
      fileSizeLimit,
      imageWidthLimit,
      imageHeightLimit,
      onChange,
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState<string>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onFileSelect = async (file: File) => {
      if (!IMAGE_VALID_TYPES.includes(file.type)) {
        setErrorMessage(
          'Invalid file type, please upload a .jpeg, .jpg or .png',
        );
        return;
      } else if (file.size > fileSizeLimit) {
        setErrorMessage('File too large, please upload a smaller file');
        return;
      }
      // read the image
      const fr = new FileReader();
      const fileLoadPromise = new Promise((resolve) => {
        fr.onload = resolve;
        fr.onerror = resolve;
      });
      fr.readAsDataURL(file);
      await fileLoadPromise;
      if (typeof fr.result !== 'string') {
        setErrorMessage('Unable to process file');
        return;
      }

      const img = new Image();
      const imageLoadPromise = new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      img.src = fr.result;
      await imageLoadPromise;
      if (img.width > imageWidthLimit || img.height > imageHeightLimit) {
        setErrorMessage('Image is too large, please upload a smaller image');
        return;
      }

      onChange?.(fr.result);
    };
    const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const { files } = e.dataTransfer;
      if (files.length) {
        onFileSelect(files[0]);
      }
    };
    const onDropZoneClicked = () => fileInputRef.current?.click();
    const onFileInputChange = () => {
      const { files } = fileInputRef.current || {};
      if (files?.length) {
        onFileSelect(files[0]);
      }
    };

    const renderImageUploader = () => (
      <>
        <div
          className={styles.dropZone}
          role="button"
          tabIndex={0}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          onDragLeave={(e) => e.preventDefault()}
          onDrop={onFileDrop}
          onClick={onDropZoneClicked}
          onKeyPress={(e) => e.key === 'Enter' && onDropZoneClicked()}
          ref={ref}
        >
          <div className={styles.dropZoneText}>
            <img src={uploadImage} alt="Upload" /> Drag and Drop or{' '}
            <span className={styles.dropZoneBrowse}>Browse</span> to upload
          </div>
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            onChange={onFileInputChange}
          />
        </div>
        {!!helperText && (
          <div className={styles.helperText}>
            {helperText}
            {!!(errorMessage || errorText) && (
              <span className={styles.errorMessage}>
                {errorMessage || errorText}
              </span>
            )}
          </div>
        )}
      </>
    );
    const renderImagePreview = () => (
      <div className={styles.imagePreview}>
        <img src={value} alt="" />
        <Button
          className={styles.reuploadButton}
          variant="primary"
          onClick={() => onChange?.(undefined)}
        >
          <img src={reloadImage} alt="" />
          Re-Upload
        </Button>
      </div>
    );

    return (
      <div className={styles.container}>
        <div className={styles.label}>{label}</div>
        {value ? renderImagePreview() : renderImageUploader()}
      </div>
    );
  },
);

interface FormLogoUploadProps<T extends FieldValues> {
  label: string | React.ReactNode;
  name: FlattenKeys<T>;
  form: UseFormMethods<T>;
  required?: boolean;
}

/**
 * Convinence wrapper for uploading a dealer's logo in a form
 */
export const FormLogoUpload = <T extends FieldValues>({
  label,
  name,
  form,
  required,
}: FormLogoUploadProps<T>) => (
  <Controller
    as={ImageUpload}
    control={form.control}
    name={(name as unknown) as FieldName<T>}
    label={label}
    helperText="File Types: .png, .jpg, .jpeg"
    fileSizeLimit={512 * 1024}
    imageHeightLimit={1500}
    imageWidthLimit={1500}
    rules={{ required }}
    defaultValue=""
    errorText={getValidationErrorMessage(
      get(form.errors, name as string) as FieldError | undefined,
    )}
  />
);
